/**
 * Map user rework notes onto supported motion-prompt controls.
 * These are stylistic hints only — the model cannot guarantee a result.
 */

export const REWORK_EXAMPLES = [
  'Stronger camera dolly and parallax',
  'More sideways tracking movement',
  'Less foliage movement — move the camera instead',
  'A more subtle push-in',
  'No one in the mirrors',
  'Avoid distorting furniture',
  'Try another version',
] as const;

const HINTS: Array<{ match: RegExp; fragment: string }> = [
  {
    match: /\b(stronger|more|bigger|bolder)\b.*\b(dolly|parallax|camera|movement|motion)\b|\bmore\s+parallax\b|\bstronger\s+camera\b/i,
    fragment:
      'Increase clear virtual-camera travel with pronounced natural parallax between foreground, midground and background. Do not rely on foliage animation.',
  },
  {
    match: /\b(track(?:ing)?|sideways|lateral|dolly\s+left|dolly\s+right)\b/i,
    fragment: 'Use a sideways dolly / tracking move with clear parallax across fixed architecture and furniture.',
  },
  {
    match: /\b(foliage|trees?|plants?|clouds?|vegetation)\b/i,
    fragment:
      'Minimise tree, plant and cloud animation. Camera translation must be the primary source of movement.',
  },
  {
    match: /\b(slower|slow down|slow(?:er)? camera|less speed)\b/i,
    fragment: 'Keep the camera move smooth and controlled, but still clearly travelling through space with parallax.',
  },
  {
    match: /\b(subtle|gentler|gentle|soft(?:er)?)\b.*\b(push[- ]?in|dolly|move)?|\bpush[- ]?in\b/i,
    fragment: 'Use a smooth, controlled push-in / dolly with natural parallax rather than an aggressive move.',
  },
  {
    match: /\b(stable|stability|reduce movement|less movement|minimal movement|keep the room still|static)\b/i,
    fragment:
      'Keep architecture and furniture structurally stable; movement should come from camera perspective and parallax only.',
  },
  {
    match: /\b(mirror|reflection|nobody|no one|no people|vacant)\b/i,
    fragment:
      'Keep every mirror, glass pane, and reflective surface empty. Do not show anyone in glass or mirrors pretending to be the cameraman, and do not invent a person, face, photographer, or camera in any reflection.',
  },
  {
    match: /\b(distort|warping|warp|furniture|geometry|walls)\b/i,
    fragment: 'Do not distort furniture, walls, or room geometry.',
  },
  {
    match: /\b(another version|different|try again|variation|new take)\b/i,
    fragment:
      'Try a different concrete camera path (dolly, tracking, gentle orbit, or pan-with-translation) while keeping the scene unchanged.',
  },
];

const SAFETY =
  'These instructions are stylistic preferences only and cannot guarantee a specific camera path. ' +
  'Keep the scene photorealistic and do not add, remove, or invent objects, people, or text.';

export interface ReworkPromptResult {
  prompt: string;
  mappedHints: string[];
  userNotes: string;
}

export function mapReworkInstructions(
  basePrompt: string,
  instructions: string,
): ReworkPromptResult {
  const userNotes = String(instructions || '').trim().slice(0, 500);
  const mappedHints: string[] = [];

  if (userNotes) {
    for (const hint of HINTS) {
      if (hint.match.test(userNotes) && !mappedHints.includes(hint.fragment)) {
        mappedHints.push(hint.fragment);
      }
    }
  }

  const parts = [String(basePrompt || '').trim(), ...mappedHints];
  if (userNotes) {
    parts.push(`Additional direction from the editor: ${userNotes}`);
  }
  parts.push(SAFETY);

  return {
    prompt: parts.filter(Boolean).join(' '),
    mappedHints,
    userNotes,
  };
}

export function reworkCreditCopy(
  alreadyCharged: boolean,
  projectCredits: number,
  usageCredits?: number | null,
): {
  credits: number;
  label: string;
} {
  if (typeof usageCredits === 'number' && usageCredits > 0) {
    return {
      credits: usageCredits,
      label: `${usageCredits} credit${usageCredits === 1 ? '' : 's'} to regenerate this clip`,
    };
  }
  if (alreadyCharged) {
    return {
      credits: 0,
      label: projectCredits > 0
        ? `No extra credits — included in this ${projectCredits}-credit project`
        : 'No extra credits — included in this project',
    };
  }
  return {
    credits: Math.max(0, projectCredits),
    label: projectCredits > 0
      ? `${projectCredits} credit${projectCredits === 1 ? '' : 's'} (project generation charge)`
      : 'No additional credits',
  };
}

export function renderCreditCopy(): { credits: number; label: string } {
  return {
    credits: 0,
    label: 'No extra credits — final compose is included in this project',
  };
}
